import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  viewChild
} from '@angular/core';
import { MatSidenavContainer, MatSidenavContent, MatSidenavModule } from '@angular/material/sidenav';
import { MatIcon } from '@angular/material/icon';
import { SearchbarComponent } from '../../../common/components/searchbar/searchbar.component';
import { DropdownComponent } from '../../../common/components/dropdown/dropdown.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { SbCreateRoomDialogComponent } from '../sb-create-room-dialog/sb-create-room-dialog.component';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { Router, RouterOutlet } from '@angular/router';
import { MatAccordion } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { Settings, SettingsOriginator, SettingsService } from '../../../services/settings/settings.service';
import { BehaviorSubject, combineLatest, interval, map, Observable, Subscription, tap } from 'rxjs';
import { UserCardComponent } from '../../../common/components/user-card/user-card.component';
import { RoomMembershipState } from '../../../../shared-api/roomMembershipState';
import { DialogService } from '../../../services/dialog/dialog.service';
import { DialogData } from '../../../common/components/dialog/dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { CounterBadgeComponent } from '../../../common/components/counter-badge/counter-badge/counter-badge.component';
import { InactivityService } from '../../../services/inactivity/inactivity.service';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { UserService } from '../../../services/user/user.service';
import { RoomAccessEnum } from '../../../../shared-api/room';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { ArchiveService } from '../../../services/archive/archive.service';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { blockRoom, Room as ModelRoom, unblockRoom } from '../../../model/room';
import { PresenceService } from '../../../services/presence/presence.service';
import { ChatStoreService } from '../../../services/chat-store/chat-store.service';
import { RoomHelperService } from '../../../services/room/room-helper.service';
import { RoomService } from '../../../services/room/room.service';
import { SessionService } from '../../../services/session/session.service';
import { RoomListAccessService } from '../../../services/room-list/room-list-access.service';
import { RoomInfo } from '../../../model/room-info';
import { environment } from '../../../../environments/environment';
import { SbSearchPublicRoomDialogComponent } from '../sb-search-public-room-dialog/sb-search-public-room-dialog.component';
import { FormsModule } from '@angular/forms';
import { SidebarTestIds } from './sb-client-left-sidebar.test-ids';
import { AccountService } from '../../../services/account/account.service';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership';
import { Room } from 'matrix-js-sdk';

@Component({
  selector: 'app-sb-client-left-sidebar',
  imports: [
    MatSidenavContainer,
    MatIcon,
    MatSidenavContent,
    MatSidenavModule,
    SearchbarComponent,
    DropdownComponent,
    SbCreateRoomDialogComponent,
    ButtonComponent,
    MatAccordion,
    MatCardModule,
    RouterOutlet,
    UserCardComponent,
    NgTemplateOutlet,
    CounterBadgeComponent,
    UserIconComponent,
    MatTooltip,
    MatDialogContent,
    MatDialogTitle,
    MatDialogActions,
    AsyncPipe,
    SbSearchPublicRoomDialogComponent,
    FormsModule
  ],
  templateUrl: './sb-client-left-sidebar.component.html',
  styleUrl: './sb-client-left-sidebar.component.scss',
  providers: []
})
/**
 * Komponente zur Anzeige aller Räume, in denen der Nutzer Mitglied ist.
 * Der Nutzer gilt auch in Räumen, für die eine offene Einladung vorliegt, als Mitglied.
 * Zusätzlich können neue Räume erstellt werden und es kann der Chat geändert werden,
 * dessen Nachrichten angezeigt werden.
 * Die Liste der Räume wird bei jedem {@link ClientEvent.Sync} erneuert.
 *
 * Hierdurch erfüllte AFO's:
 *
 * A_25046 - Durchsetzung der Berechtigungskonfiguration - Client
 * A_25596 - Kein wiederholtes Erfragen der Einwilligung
 *
 * Root-Element der Angular-App für den TI-Messenger Client für Sachbearbeiter im eingeloggten Zustand.
 */
export class SbClientLeftSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly deletionDialog = viewChild.required<TemplateRef<MatDialog>>('deletionDialog');
  @ViewChild('userIdElement') userIdElementRef!: ElementRef<HTMLElement>;
  @ViewChild('privateDropdown') privateChatDropDown: DropdownComponent | undefined;
  readonly publicChatDropDown = viewChild<DropdownComponent>('publicDropdown');
  readonly inviteDialog = viewChild.required<TemplateRef<any>>('inviteDialog');
  roomsToDeleteIds: string[] = [];
  isSidebarHidden = signal<boolean>(localStorage.getItem('sidenavState') !== 'opened');
  userId = '';
  userDisplayName = '';
  protected readonly testIds = SidebarTestIds;
  protected readonly matrix = inject(MatrixService);
  protected readonly accountService = inject(AccountService);
  protected readonly roomService = inject(RoomService);
  protected readonly chatStoreService = inject(ChatStoreService);
  protected readonly presenceService = inject(PresenceService);
  protected readonly sessionService = inject(SessionService);
  protected readonly matDialog = inject(MatDialog);
  protected readonly settingsOriginator = new SettingsOriginator(Settings.getInstance());
  protected readonly environment = environment;
  protected readonly roomListAccessService = inject(RoomListAccessService);
  protected readonly _searchTerm$ = new BehaviorSubject<string>('');
  protected userDisplayname$: Observable<string> = this.accountService.getUserDisplayName();
  protected readonly invitedRooms$: Observable<ModelRoom[]> = this.roomListAccessService.getRoomInfos$().pipe(
    map(roomInfos =>
      roomInfos
        .filter(roomInfo => {
          const member = this.roomListAccessService.getModelRoomMember(roomInfo.roomId, this.matrix.getUserId());
          return member?.membershipState === RoomMembershipState.Invite;
        })
        .sort((a, b) => {
          const memberA = this.roomListAccessService.getModelRoomMember(a.roomId, this.matrix.getUserId());
          const memberB = this.roomListAccessService.getModelRoomMember(b.roomId, this.matrix.getUserId());

          const tsA = memberA?.events.member?.event?.origin_server_ts ?? 0;
          const tsB = memberB?.events.member?.event?.origin_server_ts ?? 0;

          return tsB - tsA;
        })
        .map(roomInfo => roomInfo.modelRoom)
    )
  );
  protected readonly filteredPrivateRooms$: Observable<ModelRoom[]> = combineLatest([
    this.roomListAccessService.getRoomInfos$(),
    this._searchTerm$
  ]).pipe(
    map(([roomInfos, searchTerm]) =>
      roomInfos.filter(roomInfo => this.filterRoom(roomInfo, searchTerm, 'private')).map(roomInfo => roomInfo.modelRoom)
    )
  );
  protected readonly filteredPublicRooms$: Observable<ModelRoom[]> = combineLatest([
    this.roomListAccessService.getRoomInfos$(),
    this._searchTerm$
  ]).pipe(
    map(([roomInfos, searchTerm]) =>
      roomInfos.filter(roomInfo => this.filterRoom(roomInfo, searchTerm, 'public')).map(roomInfo => roomInfo.modelRoom)
    )
  );
  private readonly roomHelperService = inject(RoomHelperService);
  private readonly settingsService = inject(SettingsService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarService);
  private readonly inactivityService = inject(InactivityService);
  private readonly userService = inject(UserService);
  private readonly archiveService = inject(ArchiveService);
  private timerSub?: Subscription;

  ngOnInit(): void {
    this.startPolling();
    if (this.matrix.hasClient) {
      this.userId = this.accountService.getMyMxId();

      void (async (): Promise<void> => {
        await this.userService.loadUserIconFromSettings();
      })();

      this.sessionService
        .getSessionRoomId$()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: sessionRoomId => {
            if (sessionRoomId.length > 0) {
              void (async (): Promise<void> => {
                const room = await this.roomService.getRoom(sessionRoomId);
                this.openDropdownForRoomType(room.roomAccess as RoomAccessEnum);
              })();
            }
          }
        });

      this.checkShouldRoomsBeDeleted();

      (async (): Promise<void> => {
        await this.matrix.client.syncLeftRooms();

        this.getSettingsAndTrackingPopUp();
        this.inactivityService.startWatching();
      })();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  startPolling(): void {
    this.timerSub = interval(20000)
      .pipe(tap(() => this.presenceService.updateCurrentUserPresence()))
      .subscribe({
        error: err => console.error(err)
      });
  }

  stopPolling(): void {
    this.timerSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    if (this.userIdElementRef) {
      this.userIdElementRef.nativeElement.addEventListener('blur', () => {
        this.userIdElementRef.nativeElement.scrollLeft = 0;
      });
    }
  }

  public toggleSidebar(): void {
    this.isSidebarHidden.set(!this.isSidebarHidden());
    localStorage.setItem('sidenavState', this.isSidebarHidden() ? 'closed' : 'opened');
  }

  //TODO: Dieser Update Dialog war mal angedacht. Jedoch Wurde er nie komplett richtig implementiert und
  // die Gematik schreibt nicht vor, dass man unbedingt über Updates innerhalb des Clients informiert werden muss.
  // Warum auskommentieren und nicht direkt rauswerfen? Einfach falls der jetzt doch gewollt und benötigt wird, daher erstmal so.
  // public openUpdatesDialog(): void {
  //   const currentSettings = Settings.getInstance();
  //   const dialogData: DialogData = {
  //     title: 'Information von Updates',
  //     svgIcon: 'circle-warning',
  //     message: 'Release Notes',
  //     confirmText: 'Akzeptieren',
  //     cancelText: 'Abbrechen',
  //     cancelDataTestId: 'button_cancel_updates',
  //     confirmDataTestId: 'button_confirm_updates',
  //     subMessage: '',
  //     dialogActionTestId: 'dialog_updates_actions',
  //     dialogContentTestId: 'dialog_updates_content'
  //   };
  //   this.dialogService
  //     .openInfoDialog(dialogData, '28.6rem', '23rem')
  //     .pipe(takeUntilDestroyed(this.destroyRef))
  //     .subscribe({
  //       next: confirmed => {
  //         if (confirmed) {
  //           currentSettings.settingsConfigurations.lastClientVersion = environment.timClientVersion;
  //         }
  //       },
  //       complete: () => {
  //         Settings.setInstance(currentSettings);
  //
  //         this.settingsService.saveSettings(this.matrix.client, currentSettings);
  //       }
  //     });
  // }

  public openWarningDialog(): void {
    const currentSettings = Settings.getInstance();
    const dialogData: DialogData = {
      title: 'INFORMATION ZU IHRER PRIVATSPHÄRE',
      svgIcon: 'circle-warning',
      message:
        'Aktuell erheben wir keine Standortdaten oder andere personenbezogene Daten von Ihnen. Wir versichern Ihnen, ' +
        'dass keine Daten an Dritte weitergegeben werden. Ebenso verwenden wir keine Daten für Profiling oder zur Erstellung ' +
        'von Nutzerprofilen.',
      confirmText: 'Zur Kenntnis genommen',
      cancelDataTestId: 'button_cancel_privacy',
      confirmDataTestId: 'button_confirm_privacy',
      dialogActionTestId: 'dialog_privacy',
      dialogContentTestId: 'dialog_privacy_content'
    };
    this.dialogService
      .openWarningDialog(dialogData, '28.6rem', '30rem', true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: confirmed => {
          if (confirmed) {
            currentSettings.settingsConfigurations.tracking = true;
          }
        },
        complete: () => {
          currentSettings.settingsConfigurations.trackingPopUp = true;

          Settings.setInstance(currentSettings);
          void (async (): Promise<void> => {
            await this.settingsService.saveSettings(this.matrix.client, currentSettings);
            await this.presenceService.updateCurrentUserPresence();
          })();
        }
      });
  }

  onSearch(searchTerm: string): void {
    this._searchTerm$.next(searchTerm);
  }

  async navigateToRoom(event: Event, room: ModelRoom): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (this.roomHelperService.getRoomMembership(room.roomId) === KnownMembership.Invite) {
      console.log(this.matrix.client.getRoom(room.roomId));
      const inviteSender = this.findInviteSender(room);
      this.openInfoJoinDialog(inviteSender, room);
    } else {
      await this.router.navigate(['/rooms', room.roomId]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target as HTMLElement;
    if (target.closest('.mat-sidenav-content')) {
      event.dataTransfer!.dropEffect = 'copy';
    } else {
      event.dataTransfer!.dropEffect = 'none';
    }
  }

  async logout(): Promise<void> {
    this.dialogService.closeAllDialogs();
    this.stopPolling();
    // Der lokale Cleanup darf die IDP-Logout-Weiterleitung niemals verhindern: Scheitert
    // matrix.logout(), wird der Fehler nur geloggt und in jedem Fall zur Keycloak-Logout-Seite
    // weitergeleitet. Der Redirect selbst liegt zentral im MatrixService, damit manueller Logout
    // und Inaktivitäts-Abmeldung dieselbe IDP-Abmeldung auslösen (siehe redirectToIdpLogout).
    try {
      await this.matrix.logout();
    } catch (error) {
      console.warn(
        'matrix.logout() während Logout fehlgeschlagen — leite trotzdem zur IDP-Logout-Seite weiter:',
        error
      );
    }
    this.matrix.redirectToIdpLogout();
  }

  openSettings(): void {
    this.router.navigate([this.router.url, 'profile-settings']);
  }

  navigateToSettings(): void {
    this.matDialog.closeAll();
    this.router.navigate([this.router.url, 'profile-settings', 'notification']);
  }

  leaveAndForgetRooms(): void {
    this.archiveService.showArchiveDialog(this.roomsToDeleteIds).subscribe(async shouldArchive => {
      try {
        for (const roomToDeleteId of this.roomsToDeleteIds) {
          const room = this.roomListAccessService.getModelRoom(roomToDeleteId);
          if (room) {
            await this.leaveAndForget(room, shouldArchive);
          }
        }
      } catch (error) {
        console.warn('leaveAndForgetRooms: roomService.leaveAndForget fehlgeschlagen', error);
        this.snackbarService.error('Die Räume konnten nicht gelöscht werden');
      } finally {
        this.matDialog.closeAll();
      }
    });
  }

  collectRoomIdsForDeletion(event: { checkedId: string; isChecked: boolean }): void {
    if (event.isChecked && !this.roomsToDeleteIds.includes(event.checkedId)) {
      this.roomsToDeleteIds.push(event.checkedId);
    } else if (!event.isChecked && this.roomsToDeleteIds.includes(event.checkedId)) {
      this.roomsToDeleteIds = this.roomsToDeleteIds.filter(roomsToDeleteId => roomsToDeleteId !== event.checkedId);
    }
  }

  openVersionInfo(): void {
    this.router.navigate([this.router.url, 'version', 'product']);
  }

  acceptInvite(roomId: string, inviteSender?: { displayname: string; mxid: string }): void {
    this.handleRoomAccepted(roomId, inviteSender);
    this.matDialog.closeAll();
  }

  declineInvite(): void {
    this.matDialog.closeAll();
  }

  /**
   * A_25611 Durchführung der Löschung
   * @param room der zu 'löschende' Chat
   * @param shouldArchive Chat soll archiviert werden
   */
  public async leaveAndForget(room: ModelRoom, shouldArchive: boolean): Promise<void> {
    if (shouldArchive) {
      try {
        blockRoom(room);
        const response = await this.archiveService.createArchivePdf(room.roomId);
        this.archiveService.handleArchivingResponse(room, response);
      } catch (error) {
        console.error('leaveAndForget: Fehler bei Archivierung', error);
        throw error;
      } finally {
        unblockRoom(room);
      }
    }

    await this.roomService.leaveRoom(room.roomId);
    await this.roomService.forgetRoom(room.roomId);
  }

  hasRoomBeenAbandoned(roomId: string): boolean {
    if (!roomId) {
      throw new Error('No roomId provided when calling hasRoomBeenAbandoned');
    }
    const room = this.matrix.client.getRoom(roomId);
    if (!room) return true;

    return this.getOwnMembership(room) === KnownMembership.Leave;
  }

  getDisplayRoomName(room: ModelRoom): string {
    if (room.name && room.name.trim().length > 0) {
      return room.name;
    }

    const sender = this.findInviteSender(room);
    return `Chat mit ${sender?.displayname ?? 'Unbekannt'}`;
  }

  private openDropdownForRoomType(roomType: RoomAccessEnum): void {
    ({
      private: (): void => this.privateChatDropDown?.openPanel(),
      public: (): void => this.publicChatDropDown()?.openPanel()
    })[roomType]();
  }

  private getSettingsAndTrackingPopUp(): void {
    const currentSettings = Settings.getInstance();

    if (!currentSettings.settingsConfigurations.trackingPopUp) {
      this.openWarningDialog();
    }

    //TODO: Siehe obige TODO
    // if (currentSettings.settingsConfigurations.lastClientVersion !== environment.timClientVersion) {
    //   this.openUpdatesDialog();
    // }
  }

  private findInviteSender(room: ModelRoom): { displayname: string | undefined; mxid: string } | undefined {
    const members = room.members ?? [];
    const invitedMember = members.find(b => b.membershipState === 'invite');
    const senderMxid = invitedMember?.events.member?.event.sender;

    if (!senderMxid) return undefined;

    return {
      displayname: this.matrix.getUser(senderMxid)?.displayName,
      mxid: senderMxid
    };
  }

  private openInfoJoinDialog(
    inviteSender: { displayname: string | undefined; mxid: string } | undefined,
    room: ModelRoom
  ): void {
    const senderId = this.findInviteSender(room)?.mxid;

    this.matDialog.open(this.inviteDialog(), {
      data: {
        senderName: inviteSender?.displayname ?? 'Unbekannt',
        senderId: senderId,
        roomName: this.getDisplayRoomName(room),
        roomId: room.roomId
      },
      width: '32rem'
    });
  }

  private async handleRoomAccepted(
    roomId: string,
    inviteSender?: { displayname: string | undefined; mxid: string }
  ): Promise<void> {
    try {
      await this.roomService.joinRoom(roomId);

      const room = await this.roomService.getRoom(roomId);
      this.openDropdownForRoomType(room.roomAccess as RoomAccessEnum);

      await this.router.navigate(['/rooms', roomId]);
    } catch (e) {
      this.handleJoinRoomError(e, roomId, inviteSender);
    }
  }

  private handleJoinRoomError(
    error: unknown,
    roomId: string,
    inviteSender?: { displayname: string | undefined; mxid: string }
  ): void {
    if (!(error instanceof Error)) {
      this.showGenericJoinError();
      return;
    }

    let parsedError: { status?: number; message?: string };

    try {
      parsedError = JSON.parse(error.message);
    } catch {
      this.showGenericJoinError();
      return;
    }

    switch (parsedError.status) {
      case 404:
        if (
          parsedError.message?.includes(
            "Can't join remote room because no servers that are in the room have been provided"
          )
        ) {
          this.openError404InviteDialog(inviteSender, roomId);
          return;
        }
        break;

      case 502:
        this.openError502InviteDialog(roomId);
        return;
    }

    this.showGenericJoinError();
  }

  private showGenericJoinError(): void {
    this.snackbarService.error('Es kam zu einem Fehler beim Betreten des Chats. Versuchen Sie es später erneut.');
  }

  private openError502InviteDialog(roomId: string): void {
    const dialogData: DialogData = {
      title: 'Hier ist ein Fehler aufgetreten',
      message: 'Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut',
      svgIcon: 'circle-error',
      subMessage: `Sollte der Fehler weiterhin bestehen, könnte es daran liegen, dass der Chat nicht mehr existiert.`,
      confirmText: 'Ok',
      cancelText: 'Einladung ablehnen',
      cancelDataTestId: 'button_cancel_joinerror',
      confirmDataTestId: 'button_confirm_joinerror',
      dialogActionTestId: 'dialog_joinerror_actions',
      dialogContentTestId: 'dialog_joinerror_content'
    };
    this.dialogService
      .openErrorDialog(dialogData, '33.6rem', '28.5rem', true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: boolean) => {
          if (!resp) {
            this.denyRoomInvite(roomId);
          }
        },
        error: err => {
          this.snackbarService.error('Ein unerwarteter Fehler ist aufgetreten.');
          console.error('Unexpected error: ', err);
        },
        complete: () => {
          this.matDialog.closeAll();
        }
      });
  }

  private openError404InviteDialog(
    inviteSender: { displayname: string | undefined; mxid: string } | undefined,
    roomId: string
  ): void {
    const dialogData: DialogData = {
      title: 'Hier ist ein Fehler aufgetreten.',
      message: inviteSender
        ? ` Der Ersteller des Chats (${inviteSender.displayname}, ${inviteSender.mxid})
       hat diesen bereits verlassen, daher können Sie nicht beitreten`
        : ' Der Ersteller des Chats' + ' hat diesen bereits verlassen, daher können Sie nicht beitreten',
      svgIcon: 'circle-error',
      subMessage: `Die Chateinladung wird mit Schließen des Popups gelöscht`,
      confirmText: 'Ok',
      confirmDataTestId: 'button_confirm_joinerror',
      dialogActionTestId: 'dialog_joinerror_actions',
      dialogContentTestId: 'dialog_joinerror_content'
    };
    this.dialogService
      .openErrorDialog(dialogData, '33.6rem', '27.8rem', true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.denyRoomInvite(roomId));
  }

  /****************************************************************************
   * checkShouldRoomsBeDeleted
   ****************************************************************************/

  private checkShouldRoomsBeDeleted(): void {
    const modelRooms = this.roomListAccessService.getRoomInfosSync().map(roomInfo => roomInfo.modelRoom);
    const joinedModelRooms = modelRooms.filter(
      room => this.roomHelperService.getRoomMembership(room.roomId) === KnownMembership.Join
    );

    const roomsToDelete = this.checkForNecessaryArchiveWarning(joinedModelRooms);

    const dialogStatus = sessionStorage.getItem('hasDialogOpened');
    if (
      this.settingsOriginator.state.deletionPeriodConfigurations.isActivated &&
      roomsToDelete.length > 0 &&
      (dialogStatus === null || dialogStatus !== 'true')
    ) {
      sessionStorage.setItem('hasDialogOpened', 'true');

      // Intervall zum Sicherstellen, dass der DeletionDialog auch beim Öffnen verfügbar ist.
      const interval = setInterval(() => {
        const deletionDialog = this.deletionDialog();
        if (deletionDialog) {
          clearInterval(interval);
          this.matDialog.open(deletionDialog, {
            data: roomsToDelete,
            maxWidth: '35rem',
            disableClose: true
          });
        }
      }, 50);
    }
  }

  private checkForNecessaryArchiveWarning(rooms: ModelRoom[]): ModelRoom[] {
    const settingsInstance = Settings.getInstance();
    if (settingsInstance.deletionPeriodConfigurations.timeUntilDeletion === undefined) {
      return [];
    }
    const currentDate = new Date();
    return rooms.filter(room => {
      const lastEventDate = this.getLastEventDate(room.roomId);

      if (!lastEventDate) return false;

      const daysSinceLastEvent = RoomHelperService.calculateDaysSinceLastEvent(currentDate, lastEventDate);

      return daysSinceLastEvent >= settingsInstance.deletionPeriodConfigurations.timeUntilDeletion;
    });
  }

  private getLastEventDate(roomId: string): Date | null {
    const lastMessage = this.roomListAccessService.getRoomInfo(roomId)?.lastMessage;

    return lastMessage ? new Date(lastMessage.ts) : null;
  }

  /**
   * Lehnt Einladung ab.
   */
  private async denyRoomInvite(roomId: string): Promise<void> {
    const room = await this.roomService.leaveRoom(roomId);
    if (room) {
      await this.roomService.forgetRoom(room.roomId);
    }
  }

  /**
   * Eigene Mitgliedschaft im Raum: bevorzugt die Live-Mitgliedschaft aus dem RoomState
   * (aktuell nach erneutem Einladen) und fällt auf die gecachte selfMembership zurück,
   * falls kein Member-Eintrag vorhanden ist (z. B. verlassene Räume).
   */
  private getOwnMembership(room: Room): string | undefined {
    return room.getMember(this.matrix.getUserId())?.membership ?? room.getMyMembership();
  }

  private filterRoom(roomInfo: RoomInfo, searchTerm: string, roomAccess: RoomAccessEnum): boolean {
    const room = this.matrix.client.getRoom(roomInfo.roomId);
    if (!room) {
      return false;
    }
    const membership = this.getOwnMembership(room);
    if (membership === KnownMembership.Invite) {
      return false;
    }
    const isJoin = membership === KnownMembership.Join;
    const isLeave = membership === KnownMembership.Leave;

    const modelRoom = roomInfo.modelRoom;

    return (
      !this.roomService.isRoomForgotten(modelRoom.roomId) &&
      modelRoom.roomAccess === roomAccess &&
      modelRoom.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (isJoin || isLeave)
    );
  }
}
